import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './BulkRiskCalculator.css';
import Dashboard from './Dashboard';
import { API_ENDPOINTS } from '../config';
import { ACCOUNT_SIZE_PRESETS, DEFAULT_ACCOUNT_SIZE, DEFAULT_THRESHOLD, getThresholdForAccountSize } from '../utils/accountSizes';
import { sortStrategies } from '../utils/strategySort';
import { resolveStrategyDisplayName } from './dashboard/resolveStrategyDisplayName';
import { IconLock } from './Icons';
import { PnlContractsInfoButton } from './PnlScalingInfoModal';
import BetaEndedCard from './BetaEndedCard';
import { useAuth } from '../contexts/AuthContext';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';

/** Max bulk rows processed at once (each row = 2 parallel /api/analyze calls). */
const BULK_ANALYZE_ROW_CONCURRENCY = 4;

/** Demo rows for non–Pro preview (not persisted). */
function getProTeaserBulkRows(consistencyPercent, riskMode) {
  const c = Number(consistencyPercent) || 50;
  return [
    {
      id: 1,
      accountName: 'PAAPEX3982600000001',
      strategy: 'Grass Fed Prime Beef',
      contractType: 'MNQ',
      consistencyPercent: c,
      accountSize: riskMode === 'apexMae' ? 50000 : 50000,
      contracts: 3,
      maxDrawdown: riskMode === 'apexMae' ? '' : 2500,
      currentBalance: riskMode === 'apexMae' ? 52500 : '',
      startOfDayProfit: riskMode === 'apexMae' ? 0 : '',
      safetyNet: riskMode === 'apexMae' ? 2500 : '',
      profitSinceLastPayout: '',
    },
    {
      id: 2,
      accountName: 'PAAPEX3982600000002',
      strategy: 'ES Momentum Scalper',
      contractType: 'MNQ',
      consistencyPercent: c,
      accountSize: riskMode === 'apexMae' ? 50000 : 50000,
      contracts: 5,
      maxDrawdown: riskMode === 'apexMae' ? '' : 2500,
      currentBalance: riskMode === 'apexMae' ? 51200 : '',
      startOfDayProfit: riskMode === 'apexMae' ? 0 : '',
      safetyNet: riskMode === 'apexMae' ? 2500 : '',
      profitSinceLastPayout: '',
    },
    {
      id: 3,
      accountName: 'PAAPEX3982600000003',
      strategy: 'RTY Breakout Strategy',
      contractType: 'MNQ',
      consistencyPercent: c,
      accountSize: riskMode === 'apexMae' ? 100000 : 100000,
      contracts: 10,
      maxDrawdown: riskMode === 'apexMae' ? '' : 5000,
      currentBalance: riskMode === 'apexMae' ? 105500 : '',
      startOfDayProfit: riskMode === 'apexMae' ? 0 : '',
      safetyNet: riskMode === 'apexMae' ? 5000 : '',
      profitSinceLastPayout: '',
    },
    {
      id: 4,
      accountName: 'PAAPEX3982600000004',
      strategy: 'NQ Range Trader',
      contractType: 'MNQ',
      consistencyPercent: c,
      accountSize: riskMode === 'apexMae' ? 50000 : 50000,
      contracts: 4,
      maxDrawdown: riskMode === 'apexMae' ? '' : 2500,
      currentBalance: riskMode === 'apexMae' ? 48900 : '',
      startOfDayProfit: riskMode === 'apexMae' ? 0 : '',
      safetyNet: riskMode === 'apexMae' ? 2500 : '',
      profitSinceLastPayout: '',
    },
    {
      id: 5,
      accountName: 'PAAPEX3982600000005',
      strategy: 'Volatility Expansion',
      contractType: 'NQ',
      consistencyPercent: c,
      accountSize: riskMode === 'apexMae' ? 150000 : 150000,
      contracts: 2,
      maxDrawdown: riskMode === 'apexMae' ? '' : 7500,
      currentBalance: riskMode === 'apexMae' ? 158200 : '',
      startOfDayProfit: riskMode === 'apexMae' ? 0 : '',
      safetyNet: riskMode === 'apexMae' ? 7500 : '',
      profitSinceLastPayout: '',
    },
  ];
}

function BulkRiskCalculator({
  isPro,
  showBetaEndMessage = false,
  sheetNames,
  customCombos,
  onAnalyzeBulk,
  riskMode, 
  onUpgrade,
  onNavigate,
  onManageEmailSummaries,
  autoAnalyzeAfterPopulate, 
  csvBulkRows,
  csvFileNames: propCsvFileNames,
  onClearCsvData,
  bulkRows,
  setBulkRows,
  bulkResultsRisk,
  bulkResultsApexMae,
  setBulkResultsRisk,
  setBulkResultsApexMae,
  consistencyPercent = 50,
  onConsistencyPercentChange,
  onResetBulkToDefault,
}) {
  const { user } = useAuth();
  const signedIn = Boolean(user?.email && String(user.email).trim());
  // Use results from props (managed by App.js for persistence)
  const resultsRisk = bulkResultsRisk;
  const resultsApexMae = bulkResultsApexMae;
  const setResultsRisk = setBulkResultsRisk;
  const setResultsApexMae = setBulkResultsApexMae;
  
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedDetailTab, setSelectedDetailTab] = useState('risk'); // 'risk' | 'apexMae'
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [csvFileNames, setCsvFileNames] = useState(null); // Track CSV file names
  const resultsRef = useRef(null);

  useEffect(() => {
    setCsvFileNames(propCsvFileNames ?? null);
  }, [propCsvFileNames]);
  const bulkAnalysisTriggerTimerRef = useRef(null);
  const loadingRef = useRef(false);

  // Get the appropriate results based on current mode
  const results = riskMode === 'risk' ? resultsRisk : resultsApexMae;

  // Clear selected result when switching modes (but keep the results)
  useEffect(() => {
    setSelectedResult(null);
  }, [riskMode]);

  const rows = useMemo(() => {
    if (isPro) return bulkRows;
    return getProTeaserBulkRows(consistencyPercent, riskMode);
  }, [isPro, bulkRows, consistencyPercent, riskMode]);

  // Populate rows from CSV data when provided (Pro only; rows live in App for persistence)
  useEffect(() => {
    if (!isPro || !csvBulkRows || csvBulkRows.length === 0) return;
    if (process.env.NODE_ENV !== 'production') {
      console.log('BulkRiskCalculator: Populating rows from CSV data', { rowCount: csvBulkRows.length });
    }
    const normalizedRows = csvBulkRows.map((row) => ({
      ...row,
      consistencyPercent: Number(row.consistencyPercent) || Number(consistencyPercent) || 50,
    }));
    setBulkRows(normalizedRows);
    if (propCsvFileNames) {
      setCsvFileNames(propCsvFileNames);
    }
    if (onClearCsvData) {
      onClearCsvData();
    }
  }, [csvBulkRows, propCsvFileNames, onClearCsvData, consistencyPercent, isPro, setBulkRows]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const isSameResultRow = useCallback((a, b) => {
    if (!a || !b) return false;
    if (a.accountNumber && b.accountNumber && a.accountNumber === b.accountNumber) return true;
    return (
      String(a.accountName || '') === String(b.accountName || '') &&
      String(a.strategy || '') === String(b.strategy || '') &&
      String(a.contractType || '') === String(b.contractType || '')
    );
  }, []);

  const selectedRiskResult = useMemo(() => {
    if (!selectedResult) return null;
    if (Array.isArray(resultsRisk)) {
      const matched = resultsRisk.find((r) => isSameResultRow(r, selectedResult));
      if (matched) return matched;
    }
    return riskMode === 'risk' ? selectedResult : null;
  }, [selectedResult, resultsRisk, riskMode, isSameResultRow]);

  const selectedApexMaeResult = useMemo(() => {
    if (!selectedResult) return null;
    if (Array.isArray(resultsApexMae)) {
      const matched = resultsApexMae.find((r) => isSameResultRow(r, selectedResult));
      if (matched) return matched;
    }
    return riskMode === 'apexMae' ? selectedResult : null;
  }, [selectedResult, resultsApexMae, riskMode, isSameResultRow]);

  const addRow = () => {
    if (!isPro) return;
    if (rows.length >= 40) return;
    setBulkRows([
      ...rows,
      {
        id: Date.now(),
        accountName: '',
        strategy: '',
        contractType: 'MNQ',
        consistencyPercent: Number(consistencyPercent) || 50,
        accountSize: riskMode === 'apexMae' ? DEFAULT_ACCOUNT_SIZE : '',
        contracts: '',
        maxDrawdown: '',
        currentBalance: '',
        startOfDayProfit: '',
        safetyNet: riskMode === 'apexMae' ? DEFAULT_THRESHOLD : '',
      },
    ]);
  };

  const removeRow = (id) => {
    if (!isPro) return;
    if (rows.length === 1) return;
    setBulkRows(rows.filter((row) => row.id !== id));
  };

  const handleResetBulkToDefault = () => {
    if (!isPro || typeof onResetBulkToDefault !== 'function') return;
    if (
      !window.confirm(
        'Clear all bulk rows, saved results, and imported CSV data? You will start with one empty row.'
      )
    ) {
      return;
    }
    setSelectedResult(null);
    setEmailSent(false);
    setEmailError(null);
    onResetBulkToDefault();
  };

  const updateRow = (id, field, value) => {
    if (!isPro) return;
    setBulkRows(
      rows.map((row) => {
        if (row.id !== id) return row;

        const updatedRow = { ...row, [field]: value };

        if (riskMode === 'apexMae' && field === 'accountSize') {
          const threshold = getThresholdForAccountSize(Number(value));
          if (threshold) {
            updatedRow.safetyNet = threshold;
          }
        }

        if (riskMode === 'apexMae' && (field === 'currentBalance' || field === 'accountSize')) {
          const currentBalance = Math.floor(Number(updatedRow.currentBalance || 0) * 100) / 100;
          const accountSize = Math.floor(Number(updatedRow.accountSize || 0) * 100) / 100;
          if (currentBalance > 0 && accountSize > 0) {
            updatedRow.startOfDayProfit = Math.max(0, Math.floor((currentBalance - accountSize) * 100) / 100);
          }
        }

        if (field === 'currentBalance' || field === 'maxDrawdown' || field === 'startOfDayProfit' || field === 'safetyNet') {
          const numValue = Number(updatedRow[field]);
          if (!isNaN(numValue) && updatedRow[field] !== '') {
            updatedRow[field] = Math.floor(numValue * 100) / 100;
          }
        }

        return updatedRow;
      })
    );
  };

  const handleSubmit = useCallback(async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setLoading(true);
    
    try {
      // Validate all rows - we need ALL fields for both modes
      const rowsWithIndex = rows.map((row, originalIndex) => ({ ...row, originalIndex: originalIndex + 1 }));
      
      // For dual-mode analysis, we need rows that have data for BOTH modes
      // Risk mode needs: accountSize, contracts, maxDrawdown
      // ApexMae mode needs: accountSize, contracts, currentBalance, startOfDayProfit, safetyNet
      const validRows = rowsWithIndex.filter(row => {
        const hasRiskData = row.accountSize && 
                           row.contracts && 
                           row.maxDrawdown !== undefined && row.maxDrawdown !== null && row.maxDrawdown !== '';
        const hasApexMaeData = row.accountSize && 
                              row.contracts && 
                              row.currentBalance !== undefined && row.currentBalance !== null && row.currentBalance !== '' &&
                              (row.startOfDayProfit !== undefined && row.startOfDayProfit !== null) &&
                              row.safetyNet !== undefined && row.safetyNet !== null && row.safetyNet !== '';
        
        // Accept row if it has data for at least one mode
        return hasRiskData || hasApexMaeData;
      });

      if (validRows.length === 0) {
        alert('Please fill in at least one complete row');
        setLoading(false);
        return;
      }

      const authHeaders = getBearerAuthHeaders();
      const jsonHeaders = {
        'Content-Type': 'application/json',
        'X-Bulk-Analysis': 'true',
        ...authHeaders,
      };

      const analyzeDualModeRow = async (row) => {
        if (!row.strategy || row.strategy === '' || row.strategy === 'No strategy assigned') {
          return {
            riskResult: {
              metrics: {
                riskScore: 'N/A',
                blowAccountStatus: 'GO',
                riskLevel: 'UNKNOWN',
                riskMessage: 'No strategy assigned',
              },
            },
            apexMaeResult: {
              metrics: {
                riskScore: 'N/A',
                blowAccountStatus: 'GO',
                riskLevel: 'UNKNOWN',
                riskMessage: 'No strategy assigned',
              },
            },
          };
        }

        const effectiveConsistencyPercent = Number(row.consistencyPercent) || Number(consistencyPercent) || 50;

        const riskPromise = fetch(API_ENDPOINTS.analyze, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            sheetName: row.strategy,
            contractType: row.contractType,
            accountSize: parseFloat(row.accountSize),
            contracts: parseFloat(row.contracts),
            userEmail: user?.email || null,
            consistencyPercent: effectiveConsistencyPercent,
            maxDrawdown: parseFloat(row.maxDrawdown),
            startOfDayProfit: null,
            safetyNet: null,
          }),
        })
          .then(async (res) => {
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
              return {
                metrics: {
                  error: data.error || 'Analysis failed',
                  riskScore: 'N/A',
                  blowAccountStatus: 'ERROR',
                  riskMessage: data.error || 'Analysis failed',
                },
              };
            }
            return data;
          })
          .catch(() => ({
            metrics: {
              error: 'Analysis failed',
              riskScore: 'N/A',
              blowAccountStatus: 'ERROR',
              riskMessage: 'Analysis failed',
            },
          }));

        const apexMaePromise = fetch(API_ENDPOINTS.analyze, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            sheetName: row.strategy,
            contractType: row.contractType,
            accountSize: parseFloat(row.accountSize),
            contracts: parseFloat(row.contracts),
            userEmail: user?.email || null,
            consistencyPercent: effectiveConsistencyPercent,
            maxDrawdown: null,
            startOfDayProfit: parseFloat(row.startOfDayProfit),
            safetyNet: getThresholdForAccountSize(Number(row.accountSize)),
          }),
        })
          .then(async (res) => {
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
              return {
                metrics: {
                  error: data.error || 'Analysis failed',
                  riskScore: 'N/A',
                  blowAccountStatus: 'ERROR',
                  riskMessage: data.error || 'Analysis failed',
                },
              };
            }
            return data;
          })
          .catch(() => ({
            metrics: {
              error: 'Analysis failed',
              riskScore: 'N/A',
              blowAccountStatus: 'ERROR',
              riskMessage: 'Analysis failed',
            },
          }));

        const [riskResult, apexMaeResult] = await Promise.all([riskPromise, apexMaePromise]);
        return { riskResult, apexMaeResult };
      };

      const dualModeResults = [];
      for (let i = 0; i < validRows.length; i += BULK_ANALYZE_ROW_CONCURRENCY) {
        const chunk = validRows.slice(i, i + BULK_ANALYZE_ROW_CONCURRENCY);
        const chunkResults = await Promise.all(chunk.map((row) => analyzeDualModeRow(row)));
        dualModeResults.push(...chunkResults);
      }
      
      // Format results for both modes
      const formattedRiskResults = dualModeResults.map((result, index) => ({
        ...validRows[index],
        accountNumber: validRows[index].originalIndex,
        strategy: validRows[index].strategy || 'No strategy assigned',
        metrics: result.riskResult.metrics
      }));
      
      const formattedApexMaeResults = dualModeResults.map((result, index) => ({
        ...validRows[index],
        accountNumber: validRows[index].originalIndex,
        strategy: validRows[index].strategy || 'No strategy assigned',
        metrics: result.apexMaeResult.metrics
      }));
      
      // Store both sets of results
      setResultsRisk(formattedRiskResults);
      setResultsApexMae(formattedApexMaeResults);
      
      // Send email summary if user is logged in and email summaries are enabled
      // Merge both result sets so email has both Blowout Risk and % Rule data
      const mergedResults = formattedRiskResults.map((riskResult, index) => {
        const apexResult = formattedApexMaeResults[index];
        return {
          ...riskResult,
          // Add apexMae metrics alongside risk metrics
          apexMaeMetrics: apexResult?.metrics || {}
        };
      });
      
      if (user?.email) {
        // Check if email summaries are enabled for this user
        const preferenceKey = `email_summaries_enabled_${user.email}`;
        const emailSummariesEnabled = localStorage.getItem(preferenceKey) !== 'false'; // Default to true
        
        if (emailSummariesEnabled) {
          try {
            if (process.env.NODE_ENV !== 'production') {
              console.log('Sending email with CSV file names:', csvFileNames);
            }
            const emailResponse = await fetch(API_ENDPOINTS.sendRiskSummary, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
              body: JSON.stringify({
                email: user.email,
                results: mergedResults,
                riskMode: 'both', // Send both risk types
                csvFileNames: csvFileNames || null
              })
            });
            
            const emailData = await emailResponse.json();
            if (emailData.success && emailData.emailSent) {
              setEmailSent(true);
              setEmailError(null);
            } else {
              setEmailSent(false);
              setEmailError(emailData.message || 'Email could not be sent');
            }
          } catch (emailErr) {
            console.error('Error sending email summary:', emailErr);
            setEmailSent(false);
            setEmailError('Failed to send email summary');
          }
        } else {
          // Email summaries are disabled, don't send
          setEmailSent(false);
          setEmailError(null);
        }
      }
      
      // Auto-scroll to results after a short delay to ensure DOM is updated
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } catch (err) {
      console.error('Bulk analysis error:', err);
      alert('Error analyzing bulk data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [rows, user, csvFileNames, setResultsRisk, setResultsApexMae, consistencyPercent]);

  // Expose handleSubmit for external triggering (from CSV upload)
  useEffect(() => {
    const BULK_AUTO_ANALYSIS_DEBOUNCE_MS = 650;

    const runBulkAutoAnalysis = () => {
      if (loadingRef.current) {
        if (window.setAnalyzingState) window.setAnalyzingState(false);
        return;
      }

      if (window.setAnalyzingState) {
        window.setAnalyzingState(true);
      }

      const rowsWithIndex = rows.map((row, originalIndex) => ({ ...row, originalIndex: originalIndex + 1 }));
      const validRows = rowsWithIndex.filter((row) => {
        if (riskMode === 'risk') {
          return (
            row.accountSize &&
            row.contracts &&
            row.maxDrawdown !== undefined &&
            row.maxDrawdown !== null &&
            row.maxDrawdown !== ''
          );
        }
        return (
          row.accountSize &&
          row.contracts &&
          row.currentBalance !== undefined &&
          row.currentBalance !== null &&
          row.currentBalance !== '' &&
          row.startOfDayProfit !== undefined &&
          row.startOfDayProfit !== null &&
          row.safetyNet !== undefined &&
          row.safetyNet !== null &&
          row.safetyNet !== ''
        );
      });

      if (validRows.length > 0) {
        setTimeout(() => {
          handleSubmit(null).finally(() => {
            setTimeout(() => {
              if (window.setAnalyzingState) {
                window.setAnalyzingState(false);
              }
            }, 500);
          });
        }, 400);
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('No valid rows found for auto-analysis. Rows:', rows);
          console.log('Risk mode:', riskMode);
        }
        if (window.setAnalyzingState) {
          window.setAnalyzingState(false);
        }
      }
    };

    window.triggerBulkAnalysis = () => {
      if (bulkAnalysisTriggerTimerRef.current) {
        clearTimeout(bulkAnalysisTriggerTimerRef.current);
      }
      bulkAnalysisTriggerTimerRef.current = setTimeout(() => {
        bulkAnalysisTriggerTimerRef.current = null;
        runBulkAutoAnalysis();
      }, BULK_AUTO_ANALYSIS_DEBOUNCE_MS);
    };

    return () => {
      if (bulkAnalysisTriggerTimerRef.current) {
        clearTimeout(bulkAnalysisTriggerTimerRef.current);
        bulkAnalysisTriggerTimerRef.current = null;
      }
      delete window.triggerBulkAnalysis;
    };
  }, [rows, riskMode, handleSubmit]);

  return (
    <div className={`bulk-calculator ${!isPro ? 'bulk-calculator-locked' : ''}`}>
      {!isPro && (
        showBetaEndMessage && signedIn ? (
          <BetaEndedCard variant="overlay" />
        ) : !signedIn ? (
        <div className="pro-lock-overlay">
          <div className="pro-lock-content">
            <div className="pro-lock-icon">
              <IconLock size={48} />
            </div>
            <h3 className="pro-lock-title">Bulk Risk Assessment</h3>
            <p className="pro-lock-subtitle">Sign in to continue</p>
            <p className="pro-lock-description">
              Sign in to analyze up to 40 account and strategy combinations at once with RiskLo Pro.
            </p>
            <button
              type="button"
              className="pro-lock-upgrade-button"
              onClick={() => onNavigate && onNavigate('login')}
            >
              Sign in
            </button>
          </div>
        </div>
        ) : (
        <div className="pro-lock-overlay">
          <div className="pro-lock-content">
            <div className="pro-lock-icon">
              <IconLock size={48} />
            </div>
            <h3 className="pro-lock-title">Bulk Risk Assessment</h3>
            <p className="pro-lock-subtitle">This feature is available in RiskLo Pro</p>
            <p className="pro-lock-description">Upgrade to analyze up to 40 account + strategy combinations at once</p>
            <button type="button" className="pro-lock-upgrade-button" onClick={onUpgrade}>
              Upgrade to RiskLo Pro
            </button>
          </div>
        </div>
        )
      )}
      <div className="bulk-header">
        <div className="bulk-header-top">
          <div className="bulk-header-text">
            <h2 className="bulk-title">Bulk Risk Assessment (RiskLo Pro)</h2>
            <p className="bulk-subtitle">Analyze multiple account configurations at once</p>
          </div>
          {isPro && typeof onResetBulkToDefault === 'function' && (
            <button
              type="button"
              className="bulk-reset-default-btn"
              onClick={handleResetBulkToDefault}
              disabled={loading}
            >
              Reset default
            </button>
          )}
        </div>
        {riskMode === 'apexMae' && (
          <div style={{ marginTop: '0.75rem', maxWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)' }}>
              Default % Rule
            </label>
            <select
              className="bulk-input"
              value={Number(consistencyPercent) || 50}
              onChange={(e) => onConsistencyPercentChange && onConsistencyPercentChange(Number(e.target.value))}
              disabled={!isPro}
            >
              {[20, 30, 40, 50, 55, 60].map((percent) => (
                <option key={percent} value={percent}>
                  {percent}%
                </option>
              ))}
            </select>
            <div style={{ marginTop: '0.3rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)' }}>
              Used for new rows and CSV imports. Each row can override this.
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="bulk-form">
        <div className="bulk-table-container" style={{ position: 'relative' }}>
          {loading && (
            <div className="bulk-analyzing-overlay">
              <div className="bulk-analyzing-content">
                <div className="bulk-analyzing-spinner"></div>
                <div className="bulk-analyzing-text">Analyzing...</div>
              </div>
            </div>
          )}
          <table
            className={`bulk-table ${riskMode === 'apexMae' ? 'bulk-table-apex-mae' : 'bulk-table-blowout'}`}
          >
            <thead>
              <tr>
                <th className="account-number-header">#</th>
                <th className="bulk-col-account">Account Name</th>
                <th className="bulk-col-strategy">Strategy</th>
                <th className="bulk-col-contract">Contract Type</th>
                <th className="bulk-col-account-size">Account Size ($)</th>
                <th
                  className={`bulk-table-th-contracts bulk-col-contracts-count${
                    riskMode === 'apexMae' ? ' bulk-th-contracts-apex' : ''
                  }`}
                >
                  <div className="bulk-th-contracts-stack" title="Default Contract Size Multiplier">
                    <span className="bulk-th-contracts-row1">Default Contract</span>
                    <div className="bulk-th-contracts-row2">
                      <span className="bulk-th-contracts-row2-label">Size Multiplier</span>
                      <PnlContractsInfoButton className="bulk-pnl-info-btn" iconSize={14} />
                    </div>
                  </div>
                </th>
                {riskMode === 'risk' ? (
                  <th className="bulk-col-max-dd">Max Drawdown ($)</th>
                ) : (
                  <>
                    <th className="bulk-percent-col bulk-col-apex-rule">% Rule</th>
                    <th className="bulk-col-apex-balance">Current Balance ($)</th>
                    <th className="bulk-col-apex-sod">Start-of-Day Profit ($)</th>
                  </>
                )}
                <th className="bulk-col-actions" aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="account-number-cell">
                    <span className="account-number">{index + 1}</span>
                  </td>
                  <td className="bulk-col-account">
                    <textarea
                      value={row.accountName || ''}
                      onChange={(e) =>
                        updateRow(row.id, 'accountName', e.target.value.replace(/[\r\n]+/g, ''))
                      }
                      className="bulk-input bulk-input-account-name"
                      placeholder="e.g., PAAPEX3982600000002"
                      disabled={!isPro}
                      title={String(row.accountName || '').trim() || undefined}
                      autoComplete="off"
                      spellCheck={false}
                      rows={3}
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                      }}
                    />
                  </td>
                  <td className="bulk-col-strategy">
                    <select
                      value={row.strategy}
                      onChange={(e) => updateRow(row.id, 'strategy', e.target.value)}
                      className="bulk-input bulk-input-strategy"
                      required={index === 0}
                      disabled={!isPro}
                      title={String(row.strategy || '').trim() || undefined}
                    >
                      <option value="">Select...</option>
                      {sortStrategies(sheetNames, { preserveOrder: true }).map(name => (
                        <option key={name} value={name}>
                          {resolveStrategyDisplayName(name, customCombos)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="bulk-col-contract">
                    <select
                      value={row.contractType}
                      onChange={(e) => updateRow(row.id, 'contractType', e.target.value)}
                      className="bulk-input bulk-input-contract"
                      disabled={!isPro}
                    >
                      <option value="NQ">NQ</option>
                      <option value="MNQ">MNQ</option>
                    </select>
                  </td>
                  <td className="bulk-col-account-size">
                    {riskMode === 'apexMae' ? (
                      <select
                        value={row.accountSize}
                        onChange={(e) => updateRow(row.id, 'accountSize', e.target.value)}
                        className="bulk-input bulk-input-account-size"
                        required={index === 0}
                        disabled={!isPro}
                      >
                        {ACCOUNT_SIZE_PRESETS.map((preset) => (
                          <option key={`${preset.value}-${preset.label}`} value={preset.value}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="number"
                        value={row.accountSize}
                        onChange={(e) => updateRow(row.id, 'accountSize', e.target.value)}
                        className="bulk-input"
                        placeholder="50000"
                        step="0.01"
                        required={index === 0}
                        disabled={!isPro}
                      />
                    )}
                  </td>
                  <td className="bulk-col-contracts-count">
                    <input
                      type="number"
                      value={row.contracts}
                      onChange={(e) => updateRow(row.id, 'contracts', e.target.value)}
                      className="bulk-input bulk-input-contracts-count"
                      placeholder="1"
                      min="1"
                      step="1"
                      required={index === 0}
                      disabled={!isPro}
                    />
                  </td>
                  {riskMode === 'risk' ? (
                    <td className="bulk-col-max-dd">
                      <input
                        type="number"
                        value={row.maxDrawdown}
                        onChange={(e) => updateRow(row.id, 'maxDrawdown', e.target.value)}
                        className="bulk-input"
                        placeholder="2500"
                        step="0.01"
                        required={index === 0}
                        disabled={!isPro}
                      />
                    </td>
                  ) : (
                    <>
                      <td className="bulk-col-apex-rule">
                        <select
                          value={Number(row.consistencyPercent) || 50}
                          onChange={(e) => updateRow(row.id, 'consistencyPercent', Number(e.target.value))}
                          className="bulk-input bulk-percent-select"
                          disabled={!isPro}
                        >
                          {[20, 30, 40, 50, 55, 60].map((percent) => (
                            <option key={percent} value={percent}>
                              {percent}%
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="bulk-col-apex-balance">
                        <input
                          type="number"
                          value={row.currentBalance}
                          onChange={(e) => updateRow(row.id, 'currentBalance', e.target.value)}
                          className="bulk-input"
                          placeholder="52500"
                          step="0.01"
                          min="0"
                          required={index === 0}
                          disabled={!isPro}
                        />
                      </td>
                      <td className="bulk-col-apex-sod">
                        <input
                          type="number"
                          value={row.startOfDayProfit}
                          readOnly
                          className="bulk-input"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                          disabled={!isPro}
                        />
                      </td>
                    </>
                  )}
                  <td className="bulk-col-actions">
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="remove-row-btn"
                        disabled={!isPro}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bulk-actions">
          {user?.email && typeof onManageEmailSummaries === 'function' && (
            <button
              type="button"
              className="bulk-email-settings-btn"
              onClick={onManageEmailSummaries}
              disabled={!isPro}
            >
              Enable/Disable Email Summaries
            </button>
          )}
          {isPro && typeof onResetBulkToDefault === 'function' && (
            <button
              type="button"
              className="bulk-reset-default-btn"
              onClick={handleResetBulkToDefault}
              disabled={loading}
            >
              Reset default
            </button>
          )}
          {rows.length < 40 && (
            <button
              type="button"
              onClick={addRow}
              className="add-row-btn"
              disabled={!isPro}
            >
              + Add Row
            </button>
          )}
          <button
            type="submit"
            className="analyze-bulk-btn"
            disabled={loading || !isPro}
          >
            {loading ? 'Analyzing...' : `Analyze ${rows.filter(r => {
              if (riskMode === 'risk') {
                return r.strategy && r.accountSize && r.contracts && r.maxDrawdown;
              } else {
                return r.strategy && r.accountSize && r.contracts && r.startOfDayProfit !== undefined && r.startOfDayProfit !== null;
              }
            }).length} Configuration(s)`}
          </button>
        </div>
      </form>

      {results && (
        <div className="bulk-results" ref={resultsRef}>
          <h3>
            {riskMode === 'risk' ? 'Blowout Risk Results' : '% Rule Results'}
            {csvFileNames && (
              <span style={{ fontWeight: 400, fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.7)' }}>
                {' '}({csvFileNames.accountCsv && csvFileNames.strategyCsv 
                  ? `${csvFileNames.accountCsv} and ${csvFileNames.strategyCsv}` 
                  : csvFileNames.accountCsv || csvFileNames.strategyCsv || ''})
              </span>
            )}
          </h3>

          {/* Blown warning (Blowout Risk mode only) */}
          {riskMode === 'risk' && results.some(r => {
            const currentBal = Number(r.currentBalance);
            const maxDd = Number(r.maxDrawdown);
            return Number.isFinite(currentBal) && Number.isFinite(maxDd) && currentBal < maxDd;
          }) && (
            <div className="bulk-blown-warning">
              <strong>BLOWN:</strong> One or more accounts show that the current balance is less than the max trailing drawdown. These accounts should be treated as blown and require immediate attention.
            </div>
          )}
          
          {/* Email confirmation message */}
          {emailSent && user?.email && (
            <div className="email-confirmation" style={{
              padding: '1rem',
              marginBottom: '1.5rem',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              color: '#10b981'
            }}>
              <strong>✓ Email sent:</strong> We've processed your CSVs and emailed a risk summary to <strong>{user.email}</strong>.
            </div>
          )}
          
          {emailError && user?.email && (
            <div className="email-error" style={{
              padding: '1rem',
              marginBottom: '1.5rem',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              color: '#f59e0b'
            }}>
              <strong>⚠ Note:</strong> Analysis completed successfully, but we couldn't send the email summary. {emailError}
            </div>
          )}
          
          <div className="results-grid">
            {results.map((result, index) => (
              (() => {
                // BLOWN logic: Only for Blowout Risk mode (risk), when current balance < max trailing drawdown
                const currentBal = Number(result.currentBalance);
                const maxDd = Number(result.maxDrawdown);
                const isBlown = riskMode === 'risk' && Number.isFinite(currentBal) && Number.isFinite(maxDd) && currentBal < maxDd;
                
                // For % Rule (apexMae), only use GO/NO GO based on consistency comparison
                const exceedsMae = !!result.metrics?.apexMaeComparison?.exceedsMae;
                const statusText = riskMode === 'apexMae'
                  ? (exceedsMae ? 'NO GO' : 'GO')
                  : (isBlown ? 'BLOWN' : (result.metrics?.blowAccountStatus || 'N/A'));
                const statusColor = riskMode === 'apexMae'
                  ? (exceedsMae ? '#ef4444' : '#10b981')
                  : (isBlown ? '#ef4444' : (result.metrics?.blowAccountColor || '#6b7280'));

                // Determine card status class for glow effect
                const cardStatusClass = riskMode === 'apexMae'
                  ? (exceedsMae ? 'no-go' : 'go')
                  : (isBlown ? 'blown' : (result.metrics?.blowAccountStatus === 'NO GO' ? 'no-go' : 'go'));

                return (
              <div 
                key={index} 
                className={`result-card ${isBlown ? 'blown' : ''} ${cardStatusClass}`}
                onClick={() => {
                  setSelectedResult(result);
                  setSelectedDetailTab(riskMode === 'apexMae' ? 'apexMae' : 'risk');
                }}
              >
                <div className="result-header">
                  <div className="result-left">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span className="account-number-badge">Account #{result.accountNumber}</span>
                      {result.accountName && (
                        <span className="account-name-display">
                          {result.accountName}
                        </span>
                      )}
                    </div>
                    <span className="result-strategy">{result.strategy}</span>
                  </div>
                  <span 
                    className="result-status"
                    style={{ 
                      color: statusColor
                    }}
                  >
                    {statusText}
                  </span>
                </div>
                <div className="result-metrics">
                  {riskMode === 'risk' && (
                    <>
                      <div className="result-metric">
                        <span>Highest Loss:</span>
                        <span>${result.metrics?.highestLoss?.toFixed(2) || 'N/A'}</span>
                      </div>
                      <div className="result-metric">
                        <span>Risk Score:</span>
                        <span>{result.metrics?.riskScore || 'N/A'}/100</span>
                      </div>
                      {result.metrics?.blowAccountProbability !== null && result.metrics?.blowAccountProbability !== undefined && (
                        <div className="result-metric">
                          <span>Exceed Probability:</span>
                          <span style={{ 
                            color: result.metrics.blowAccountProbability > 0 
                              ? (result.metrics.blowAccountStatus === 'NO GO' ? '#ef4444' : '#f59e0b')
                              : '#10b981',
                            fontWeight: '600'
                          }}>
                            {result.metrics.blowAccountProbability.toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  {riskMode === 'apexMae' && result.metrics?.apexMaeComparison && (
                    <div className="result-metrics-split">
                      {/* Left Column: Consistency (Loss) Information */}
                      <div className="result-metrics-column result-metrics-column-mae">
                        <div className="result-column-header">
                          <span className="result-column-title">Consistency (Loss)</span>
                        </div>
                        <div className="result-metric-group">
                          <div className="result-metric">
                            <span>Highest Loss:</span>
                            <span className="result-metric-value">${result.metrics?.highestLoss?.toFixed(2) || 'N/A'}</span>
                          </div>
                          <div className="result-metric">
                            <span>Consistency Limit:</span>
                            <span className="result-metric-value" style={{ 
                              color: result.metrics.apexMaeComparison.exceedsMae ? '#ef4444' : '#10b981' 
                            }}>
                              ${result.metrics.apexMaeComparison.maxMaePerTrade?.toFixed(2) || 'N/A'}
                            </span>
                          </div>
                          {result.metrics.apexMaeComparison.maeBreachProbability !== undefined && (
                            <div className="result-metric">
                              <span>Exceed Prob:</span>
                              <span className="result-metric-value" style={{ 
                                color: result.metrics.apexMaeComparison.maeBreachProbability > 0 
                                  ? (result.metrics.apexMaeComparison.exceedsMae ? '#ef4444' : '#f59e0b')
                                  : '#10b981',
                                fontWeight: '600'
                              }}>
                                {result.metrics.apexMaeComparison.maeBreachProbability.toFixed(1)}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Right Column: Windfall (Fastest Payout Path) Information */}
                      {result.metrics?.windfallRule && (
                        <div className="result-metrics-column result-metrics-column-windfall">
                          <div className="result-column-header">
                            <span className="result-column-title">Fastest Payout Path</span>
                          </div>
                          <div className="result-metric-group">
                            <div className="result-metric">
                              <span>Highest Profit Day:</span>
                              <span className="result-metric-value">${result.metrics.windfallRule.maxProfitDay ? parseFloat(result.metrics.windfallRule.maxProfitDay).toFixed(2) : (result.metrics?.maxProfit?.toFixed(2) || 'N/A')}</span>
                            </div>
                            <div className="result-metric">
                              <span>Safe Day for Fastest Payout:</span>
                              <span className="result-metric-value" style={{ 
                                color: result.metrics.windfallRule.payoutPathStatus === 'ALIGNED' ? '#10b981' : '#f59e0b' 
                              }}>
                                ${(() => {
                                  const value = result.metrics.windfallRule?.largestSafeDayForFastestPayout;
                                  // Try to parse the value - it should be a string like "780.00" from backend
                                  if (value != null && value !== '' && value !== 'null' && value !== 'undefined') {
                                    const parsed = parseFloat(String(value));
                                    if (!isNaN(parsed)) {
                                      return parsed.toFixed(2);
                                    }
                                  }
                                  // Fallback to constant: 30% of $2,600 = $780
                                  return '780.00';
                                })()}
                              </span>
                            </div>
                            {result.metrics.windfallRule.probabilityExceedingFastestPayoutThreshold !== undefined && result.metrics.windfallRule.probabilityExceedingFastestPayoutThreshold !== null && (
                              <div className="result-metric">
                                <span>Exceed Prob:</span>
                                <span className="result-metric-value" style={{ 
                                  color: result.metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 60 ? '#ef4444' : 
                                         result.metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 30 ? '#f59e0b' : '#10b981',
                                  fontWeight: '600'
                                }}>
                                  {result.metrics.windfallRule.probabilityExceedingFastestPayoutThreshold.toFixed(1)}%
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="result-click-hint">Click for details →</div>
              </div>
                );
              })()
            ))}
          </div>
        </div>
      )}

      {selectedResult && (
        <div className="result-detail-modal" onClick={() => setSelectedResult(null)}>
          <div className="result-detail-content" onClick={(e) => e.stopPropagation()}>
            <div className="result-detail-header">
              <h3>
                Account #{selectedResult.accountNumber}
                {selectedResult.accountName && (
                  <span style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '0.9rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginLeft: '0.5rem',
                    fontWeight: 'normal'
                  }}>
                    ({selectedResult.accountName})
                  </span>
                )}
                {' - '}
                {selectedResult.strategy}
              </h3>
              <button className="close-detail-btn" onClick={() => setSelectedResult(null)}>×</button>
            </div>
            <div className="result-detail-tabs">
              <button
                type="button"
                className={`result-detail-tab ${selectedDetailTab === 'risk' ? 'active' : ''}`}
                onClick={() => setSelectedDetailTab('risk')}
              >
                Blowout Risk Results
              </button>
              <button
                type="button"
                className={`result-detail-tab ${selectedDetailTab === 'apexMae' ? 'active' : ''}`}
                onClick={() => setSelectedDetailTab('apexMae')}
              >
                % Rule Risk
              </button>
            </div>
            <Dashboard
              metrics={(selectedDetailTab === 'apexMae' ? selectedApexMaeResult : selectedRiskResult)?.metrics}
              riskMode={selectedDetailTab === 'apexMae' ? 'apexMae' : 'risk'}
              consistencyPercent={Number(selectedResult?.consistencyPercent) || Number(consistencyPercent) || 50}
              formData={{
                contracts: selectedResult.contracts,
                contractType: selectedResult.contractType,
                accountSize: selectedResult.accountSize,
                maxDrawdown: selectedResult.maxDrawdown,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkRiskCalculator;

